//@ts-check
import { searchOrigin } from '../Results/constants';
import { getResearcherText } from 'utils/constants';

export const hiddenKeys = Object.freeze({
  date: {
    key: 'date',
    description: 'Date',
  },
  organisationCategory: {
    key: 'organisationCategory',
    description: 'Organisation Category',
  },
});

export const categoryKeys = Object.freeze({
  funding: { key: 'funding', description: 'Funding' },
  tradeshows: { key: 'tradeshows', description: 'Tradeshows' },
  clinicalTrials: { key: 'clinicalTrials', description: 'Clinical Trials' },
  publications: { key: 'publications', description: 'Publications' },
  profile: { key: 'profile', description: '3rd Party Data' },
});

export const workItemCategories = Object.freeze({
  funding: { key: 'funding', description: 'Funding' },
  tradeshows: { key: 'tradeshows', description: 'Tradeshows' },
  clinicalTrials: { key: 'clinicalTrials', description: 'Clinical Trials' },
  publications: { key: 'publications', description: 'Publications' },
  profile: { key: 'profile', description: '3rd Party Data' },
});

export const listsKeys = Object.freeze({
  lists: {
    key: 'lists',
    description: 'Included',
    sectionTitle: `${getResearcherText({ capitalize: true })} Lists`,
  },
  excludeLists: {
    key: 'excludeLists',
    description: 'Excluded',
    sectionTitle: `${getResearcherText({ capitalize: true })} Lists`,
  },
  organisationLists: {
    key: 'organisationLists',
    description: 'Included',
    sectionTitle: 'Organisation Lists',
  },
  organisationExcludeLists: {
    key: 'organisationExcludeLists',
    description: 'Excluded',
    sectionTitle: 'Organisation Lists',
  },
});

export const advancedKeys = Object.freeze({
  totalPublications: {
    key: 'totalPublications',
    description: 'Min. Publications',
  },
  totalCollaboratorsOverall: {
    key: 'totalCollaboratorsOverall',
    description: 'Min. Collaborators',
  },
  totalFirstNamedAuthorCount: {
    key: 'totalFirstNamedAuthorCount',
    description: 'First Author Positions',
  },
  totalLastNamedAuthorCount: {
    key: 'totalLastNamedAuthorCount',
    description: 'Last Author Positions',
  },
  totalGrants: { key: 'totalGrants', description: 'Min. Total Funding' },
  firstAuthor: { key: 'firstAuthor', description: 'First Named Author' },
  middleAuthor: { key: 'middleAuthor', description: 'Middle Named Author' },
  lastAuthor: { key: 'lastAuthor', description: 'Last Named Author' },
  threeYearHIndexAverage: {
    key: 'threeYearHIndexAverage',
    description: 'Min. Research Impact (3 Year Avg)',
  },
  kol: { key: 'kol', description: 'Key Opinion Leaders Only' },
  journal: { key: 'journal', description: 'Journals or Preprint Repository' },
});

export const advancedResearcerKeys = Object.freeze({
  validEmails: {
    key: 'validEmails',
    description: 'Only Show Results Containing An Email',
  },
  excludeRecentlyExportedEmails: {
    key: 'excludeRecentlyExportedEmails',
    description: 'Exclude Recently Exported Emails',
    tooltip:
      'Exclude exported emails from the past 3 months (maximum up to 65k most recent emails will be excluded)',
  },
});

export const fundingKeys = Object.freeze({
  dateAwarded: { key: 'dateAwarded', description: 'Funding Awarded' },
  nihApplicationType: {
    key: 'nihApplicationType',
    description: 'Application Type',
  },
  nihActivityCode: { key: 'nihActivityCode', description: 'Activity Code' },
  nihInstituteCode: { key: 'nihInstituteCode', description: 'Institute Code' },
  earlyAwardDate: { key: 'earlyAwardDate', description: 'Early Budget' },
  maxFunding: { key: 'maxFunding', description: 'Max. Funding (3 Years)' },
  minFunding: { key: 'minFunding', description: 'Min. Funding (3 Years)' },
  grantExpiry: {
    key: 'grantExpiry',
    description: 'Funding Expiring Within 3 Months',
  },
  core: { key: 'core', description: 'Core Labs Only' },
});

export const geoKeys = Object.freeze({
  organisationGeography: {
    key: 'organisationGeography',
    description: 'Region / Country',
  },
  state: { key: 'state', description: 'State' },
  city: { key: 'city', description: 'City' },
  postcode: { key: 'postcode', description: 'Zip / Postal Code' },
});

export const contactsKeys = Object.freeze({
  fullName: { key: 'fullName', description: 'Full Name' },
  lastName: { key: 'lastName', description: 'Last Name' },
  jobTitle: { key: 'jobTitle', description: 'Job title' },
  recentlyMovedOrg: {
    key: 'recentlyMovedOrg',
    description: 'Moved organisation within last six months',
  },
});

export const orgResearcherKeys = Object.freeze({
  organisationName: { key: 'organisationName', description: 'Organisation' },
  organisationNameExactMatch: {
    key: 'organisationNameExactMatch',
    description: 'Search exact names only',
  },
  email: { key: 'email', description: 'Email Domains - Max 10' },
  organisationDescription: {
    key: 'organisationDescription',
    description: 'Organisation Description',
  },
});

export const orgOrganisationKeys = Object.freeze({
  organisationName: { key: 'organisationName', description: 'Organisation' },
  organisationNameExactMatch: {
    key: 'organisationNameExactMatch',
    description: 'Search exact names only',
  },
  email: { key: 'email', description: 'Email Domains - Max 10' },
  superOrganisationSize: {
    key: 'superOrganisationSize',
    description: 'Employee Count',
  },
  superOrganisationFoundedYear: {
    key: 'superOrganisationFoundedYear',
    description: 'Year Founded',
  },
  organisationDescription: {
    key: 'organisationDescription',
    description: 'Organisation Description',
  },
  recentlyAddedOrg: {
    key: 'recentlyAddedOrg',
    description: 'Added within last month',
  },
  superOrgAcademicTotalGrants: {
    key: 'superOrgAcademicTotalGrants',
    description: 'Min. Total Funding Awards',
  },
  superOrgIndustryTotalGrants: {
    key: 'superOrgIndustryTotalGrants',
    description: 'Min. Total Funding Awards',
  },
  superOrgTotalGrants: {
    key: 'superOrgTotalGrants',
    description: 'Min. Total Funding Awards',
  },
  superOrgAcademicTotalSessions: {
    key: 'superOrgAcademicTotalSessions',
    description: 'Min. Total Tradeshows',
  },
  superOrgIndustryTotalSessions: {
    key: 'superOrgIndustryTotalSessions',
    description: 'Min. Total Tradeshows',
  },
  superOrgTotalSessions: {
    key: 'superOrgTotalSessions',
    description: 'Min. Total Tradeshows',
  },
  superOrgAcademicTotalPublications: {
    key: 'superOrgAcademicTotalPublications',
    description: 'Min. Total Publications',
  },
  superOrgIndustryTotalPublications: {
    key: 'superOrgIndustryTotalPublications',
    description: 'Min. Total Publications',
  },
  superOrgTotalPublications: {
    key: 'superOrgTotalPublications',
    description: 'Min. Total Publications',
  },
  superOrgAcademicTotalClinicalTrials: {
    key: 'superOrgAcademicTotalClinicalTrials',
    description: 'Min. Clinical Trials',
  },
  superOrgIndustryTotalClinicalTrials: {
    key: 'superOrgIndustryTotalClinicalTrials',
    description: 'Min. Clinical Trials',
  },
  superOrgTotalClinicalTrials: {
    key: 'superOrgTotalClinicalTrials',
    description: 'Min. Clinical Trials',
  },
  superOrgAcademicThreeYearTotalFunding: {
    key: 'superOrgAcademicThreeYearTotalFunding',
    description: 'Min. Funding (3 Years)',
  },
  superOrgIndustryThreeYearTotalFunding: {
    key: 'superOrgIndustryThreeYearTotalFunding',
    description: 'Min. Funding (3 Years)',
  },
  superOrgThreeYearTotalFunding: {
    key: 'superOrgThreeYearTotalFunding',
    description: 'Min. Funding (3 Years)',
  },
});

export const tradeshowResearcherKeys = Object.freeze({
  tradeshow: { key: 'tradeshow', description: 'Tradeshow' },
  thisShowOnly: {
    key: 'thisShowOnly',
    description: 'Search selected tradeshows only',
  },
  sessionType: { key: 'sessionType', description: 'Tradeshow Session Type' },
  thisSessionTypeOnly: {
    key: 'thisSessionTypeOnly',
    description: 'Only show results with selected session types',
  },
  roleType: { key: 'roleType', description: 'Tradeshow Role' },
  thisRoleTypeOnly: {
    key: 'thisRoleTypeOnly',
    description: 'Only show results with selected roles',
  },
  attendingPresentersOnly: {
    key: 'attendingPresentersOnly',
    description: 'Show all attending presenters',
  },
  tradeshowCountryRegion: {
    key: 'tradeshowCountryRegion',
    description: 'Region / Country',
  },
  tradeshowState: { key: 'tradeshowState', description: 'State / Province' },
  tradeshowCity: { key: 'tradeshowCity', description: 'City' },
});

export const tradeshowOrganisationKeys = Object.freeze({
  tradeshow: { key: 'tradeshow', description: 'Tradeshow' },
  thisShowOnly: {
    key: 'thisShowOnly',
    description: 'Search selected tradeshows only',
  },
  exhibitingShow: {
    key: 'exhibitingShow',
    description: 'Exhibiting at a tradeshow',
  },
  presentingAtTradeshow: {
    key: 'presentingAtTradeshow',
    description: 'Presenting at a tradeshow',
  },
  tradeshowCountryRegion: {
    key: 'tradeshowCountryRegion',
    description: 'Region / Country',
  },
  tradeshowState: { key: 'tradeshowState', description: 'State / Province' },
  tradeshowCity: { key: 'tradeshowCity', description: 'City' },
});

export const trialKeys = Object.freeze({
  phase: { key: 'phase', description: 'Phase' },
  overallStatus: { key: 'overallStatus', description: 'Status' },
  leadSponsor: { key: 'leadSponsor', description: 'Lead Sponsor' },
  sponsors: { key: 'sponsors', description: 'All Sponsors' },
  roles: { key: 'roles', description: 'Role' },
  startDate: { key: 'startDate', description: 'Clinical Trial Start' },
  hasClinicalTrials: {
    key: 'hasClinicalTrials',
    description: 'Listed on at least one clinical trial',
  },
  minSponsoredTrials: {
    key: 'minSponsoredTrials',
    description: 'Is a sponsor on Min number of trials',
  },
  bpMinSponsoredTrials: {
    key: 'bpMinSponsoredTrials',
    description: 'Is a sponsor on Min number of trials',
  },
  academicMinSponsoredTrials: {
    key: 'academicMinSponsoredTrials',
    description: 'Is a sponsor on Min number of trials',
  },
  phaseStartDate: {
    key: 'phaseStartDate',
    description: 'Phase Start Date',
  },
});

export const ventureCapitalKeys = Object.freeze({
  latestFundingRoundsOnly: {
    key: 'latestFundingRoundsOnly',
    description: 'Only Search the Companys latest funding Round',
  },
  fundingType: {
    key: 'fundingType',
    getDescription: filters =>
      filters[ventureCapitalKeys.latestFundingRoundsOnly.key]
        ? 'Latest Funding Round'
        : 'Funding Round',
  },
  announcedDateSelection: {
    key: 'announcedDateSelection',
    getDescription: filters =>
      filters[ventureCapitalKeys.latestFundingRoundsOnly.key]
        ? 'Latest Announced Date'
        : 'Announced Date',
  },
  announcedDatePicked: {
    key: 'announcedDatePicked',
    description: 'VC Funding Announced',
  },
  minFundingAmount: {
    key: 'minFundingAmount',
    getDescription: filters =>
      filters[ventureCapitalKeys.latestFundingRoundsOnly.key]
        ? 'Latest Min Funding Amount ($)'
        : 'Funding Round Min Amount ($)',
  },
  maxFundingAmount: {
    key: 'maxFundingAmount',
    getDescription: filters =>
      filters[ventureCapitalKeys.latestFundingRoundsOnly.key]
        ? 'Latest Max Funding Amount ($)'
        : 'Funding Round Max Amount ($)',
  },
  investors: {
    key: 'investors',
    getDescription: filters =>
      filters[ventureCapitalKeys.latestFundingRoundsOnly.key]
        ? 'Latest Investors'
        : 'Investors',
  },
  minTotalFundingRaised: {
    key: 'minTotalFundingRaised',
    description: 'Total Funding Raised Min Amount ($)',
  },
  maxTotalFundingRaised: {
    key: 'maxTotalFundingRaised',
    description: 'Total Funding Raised Max Amount ($)',
  },
});

export const acquisitionOnlyKeys = Object.freeze({
  showAcquiredCompanies: {
    key: 'showAcquiredCompanies',
    description: 'Show Acquired Companies',
  },
  showAcquiringCompanies: {
    key: 'showAcquiringCompanies',
    description: 'Show Companies that Acquire',
  },
  acquirers: { key: 'acquirers', description: 'Acquirer' },
  acquisitionType: { key: 'acquisitionType', description: 'Acquisition Type' },
  minAcquisitionPrice: {
    key: 'minAcquisitionPrice',
    description: 'Acquisition Min Amount ($)',
  },
  maxAcquisitionPrice: {
    key: 'maxAcquisitionPrice',
    description: 'Acquisition Max Amount ($)',
  },
  acquisitionDatePicked: {
    key: 'acquisitionDatePicked',
    description: 'Acquisition Date Range',
  },
  acquisitionDateSelection: {
    key: 'acquisitionDateSelection',
    description: 'Acquisition Date',
  },
});

export const purchasingKeys = Object.freeze({
  type: {
    key: 'purchaserType',
    description: 'Purchaser Type',
  },
  name: {
    key: 'purchaserName',
    description: 'Purchaser Name',
  },
  country: {
    key: 'purchaserCountry',
    description: 'Purchaser Country',
  },
  state: {
    key: 'purchaserState',
    description: 'Purchaser State / Province',
  },
  zip: {
    key: 'purchaserZip',
    description: 'Purchaser Zip / Postal Code',
  },
  supplierName: {
    key: 'supplierName',
    description: 'Supplier Name',
  },
  item: {
    key: 'item',
    description: 'Item',
  },
  date: {
    key: 'date',
    description: 'Purchase Date',
  },
  minPrice: {
    key: 'minPrice',
    description: 'Min Price (USD)',
  },
  maxPrice: {
    key: 'maxPrice',
    description: 'Max Price (USD)',
  },
  hasPrice: {
    key: 'hasPrice',
    description: 'PO Includes Price',
  },
});

export const tradeshowKeys = Object.freeze({
  type: {
    key: 'month',
    description: 'Month',
  },
  name: {
    key: 'year',
    description: 'Year',
  },
  startDate: {
    key: 'startDate',
    description: 'Start Date',
  },
  year: {
    key: 'location',
    description: 'Location',
  },
  month: {
    key: 'name',
    description: 'Name',
  },
});

export const tradeshowCalendarKeys = Object.freeze({
  year: {
    key: 'year',
    description: 'Year',
  },
  month: {
    key: 'month',
    description: 'Month',
  },
  date: {
    key: 'tradeshowDate',
    description: 'Date',
  },
  city: {
    key: 'city',
    description: 'City',
  },
  state: {
    key: 'state',
    description: 'State / Province',
  },
  regionCountry: {
    key: 'regionCountry',
    description: 'Region / Country',
  },
  tradeshowStatus: {
    key: 'tradeshowStatus',
    description: 'Tradeshow Status',
  },
  platformStatus: {
    key: 'platformStatus',
    description: 'Platform Status',
  },
  registeredInterest: {
    key: 'registeredInterest',
    description: 'Registered Interest',
  },
  tradeshowEventFormat: {
    key: 'tradeshowEventFormat',
    description: 'Event Format',
  },
});

export const listKeys = Object.freeze({
  type: {
    key: 'month',
    description: 'Month',
  },
  name: {
    key: 'year',
    description: 'Year',
  },
  startDate: {
    key: 'startDate',
    description: 'Start Date',
  },
  year: {
    key: 'year',
    description: 'Year',
  },
  month: {
    key: 'month',
    description: 'Month',
  },
});

// Org-page-only keys. `state`/`city`/`jobTitle`/`lastName` are NOT repeated
// here — geoKeys/contactsKeys (listed after this group in
// keysPerView[organisationPageSearch]) own their titles.
export const orgPageKeys = Object.freeze({
  country: {
    key: 'country',
    description: 'Region / Country',
  },
  timeAtCompany: {
    key: 'timeAtCompany',
    description: 'Time at Company',
  },
  clinicalTrialSponsor: {
    key: 'sponsorSuperOrgId',
    description: 'Trials Sponsored by this Organisation',
  },
  leadSponsor: {
    key: 'leadSponsor',
    description: 'Lead Sponsor',
  },
  collaboratorSponsor: {
    key: 'collaboratorSponsor',
    description: 'Collaborator',
  },
});

// The People/Activity "Date" dropdown. Listed last in
// keysPerView[organisationPageSearch] so it outranks purchasingKeys' "Purchase
// Date". The org Purchasing tab only counts tokens from this bag; its own date
// picker carries the "Purchase Date" wording, and its exports use purchasingSearch.
const orgPageDateKeys = Object.freeze({
  date: hiddenKeys.date,
});

export const groupedKeys = {
  category: [
    categoryKeys.clinicalTrials.key,
    categoryKeys.funding.key,
    categoryKeys.profile.key,
    categoryKeys.publications.key,
    categoryKeys.tradeshows.key,
  ],
  namedAuthor: [
    advancedKeys.firstAuthor.key,
    advancedKeys.middleAuthor.key,
    advancedKeys.lastAuthor.key,
  ],
  workItemCategories: [
    workItemCategories.clinicalTrials.key,
    workItemCategories.funding.key,
    workItemCategories.publications.key,
    workItemCategories.profile.key,
    workItemCategories.tradeshows.key,
  ],
};

const { ...orgAdvancedKeys } = advancedKeys;
export const keysPerView = {
  [searchOrigin.researcherSearch]: [
    categoryKeys,
    hiddenKeys,
    geoKeys,
    orgResearcherKeys,
    orgOrganisationKeys,
    contactsKeys,
    fundingKeys,
    ventureCapitalKeys,
    acquisitionOnlyKeys,
    tradeshowResearcherKeys,
    tradeshowOrganisationKeys,
    trialKeys,
    listsKeys,
    advancedKeys,
    advancedResearcerKeys,
  ],
  [searchOrigin.organisationSearch]: [
    categoryKeys,
    hiddenKeys,
    geoKeys,
    orgOrganisationKeys,
    contactsKeys,
    fundingKeys,
    ventureCapitalKeys,
    acquisitionOnlyKeys,
    tradeshowResearcherKeys,
    tradeshowOrganisationKeys,
    trialKeys,
    listsKeys,
    orgAdvancedKeys,
    advancedResearcerKeys,
  ],
  [searchOrigin.activitySearch]: [
    categoryKeys,
    hiddenKeys,
    geoKeys,
    orgResearcherKeys,
    orgOrganisationKeys,
    contactsKeys,
    fundingKeys,
    ventureCapitalKeys,
    acquisitionOnlyKeys,
    tradeshowResearcherKeys,
    tradeshowOrganisationKeys,
    trialKeys,
    listsKeys,
    advancedKeys,
    advancedResearcerKeys,
  ],
  [searchOrigin.purchasingSearch]: [purchasingKeys],
  [searchOrigin.tradeshow]: [tradeshowKeys],
  [searchOrigin.tradeshowCalendar]: [tradeshowCalendarKeys],
  [searchOrigin.organisationPageSearch]: [
    orgPageKeys,
    purchasingKeys,
    advancedResearcerKeys,
    workItemCategories,
    // SCIL-17770: the org People/Activity panels now expose the main-search
    // Location/Funding/Tradeshow/Clinical-Trial/Advanced/Lists controls against
    // the shared `filters` slice, so their key definitions must advertise a
    // token here. Excluded keys (minSponsoredTrials trio) are suppressed via
    // orgPageExcludedFilters; VC/Acquisition/super-org groups are intentionally
    // not listed.
    geoKeys,
    contactsKeys,
    fundingKeys,
    tradeshowResearcherKeys,
    tradeshowOrganisationKeys,
    trialKeys,
    advancedKeys,
    listsKeys,
    orgPageDateKeys,
  ],
};

/**
 * @param {string} key
 * @returns {string}
 */
export function getSectionTitleFromKey(key) {
  if (categoryKeys[key]) return 'Category';
  if (workItemCategories[key]) return 'Category';
  if (advancedKeys[key]) return 'Advanced / KOL';
  if (fundingKeys[key]) return 'Funding';
  if (geoKeys[key]) return 'Location';
  if (contactsKeys[key]) return 'Contacts';
  if (orgResearcherKeys[key] || orgOrganisationKeys[key]) return 'Organisation';
  if (tradeshowResearcherKeys[key] || tradeshowOrganisationKeys[key])
    return 'Tradeshows';
  if (trialKeys[key]) return 'Clinical Trials';
  if (ventureCapitalKeys[key]) return 'Venture Capital';
  if (acquisitionOnlyKeys[key]) return 'Acquisitions';
  if (listsKeys[key]) return 'Lists';

  return '';
}

export const dateFields = [
  { datePicker: hiddenKeys.date.key },
  {
    datePicker: acquisitionOnlyKeys.acquisitionDateSelection.key,
    dateSelection: acquisitionOnlyKeys.acquisitionDatePicked.key,
  },
  {
    datePicker: ventureCapitalKeys.announcedDateSelection.key,
    dateSelection: ventureCapitalKeys.announcedDatePicked.key,
  },
];

const searchKeys = (() => {
  let ret = {};
  for (let view of [
    searchOrigin.organisationSearch,
    searchOrigin.researcherSearch,
    searchOrigin.activitySearch,
    searchOrigin.purchasingSearch,
    searchOrigin.tradeshow,
    searchOrigin.organisationPageSearch,
    searchOrigin.tradeshowCalendar,
  ]) {
    ret[view] = {};
    keysPerView[view].forEach(x => {
      Object.values(x).forEach(y => {
        let isGrouped = Object.keys(groupedKeys).filter(z =>
          groupedKeys[z].includes(y.key)
        );
        let field = y.key;
        let filterGroup = '';
        if (isGrouped.length > 0) {
          if (
            isGrouped[0] === 'category' &&
            view === searchOrigin.organisationPageSearch
          )
            filterGroup = isGrouped[1];
          else filterGroup = isGrouped[0];
        }
        if (filterGroup) {
          field = `${filterGroup}.${field}`;
        }
        ret[view][field] = {
          title: y.description,
          sectionTitle: y.sectionTitle
            ? y.sectionTitle
            : getSectionTitleFromKey(y.key),
          getTitle: y.getDescription ? z => y.getDescription(z) : null,
        };
      });
    });
  }
  return ret;
})();

export function GetSearchKeysByView(view) {
  return searchKeys[view];
}

export function RemoveNonEmptyDate(filters) {
  let filterKeys = Object.keys(filters);
  let dateKeys = dateFields.filter(x => {
    if (filterKeys.includes(x.datePicker)) return true;
    else return false;
  });
  if (dateKeys) {
    dateKeys.forEach(x => {
      if (
        filters[x.datePicker] === 'all' ||
        (x.dateSelection
          ? filters[x.datePicker] === 'custom' && !filters[x.dateSelection]
          : filters[x.datePicker] === 'custom')
      ) {
        delete filters[x.datePicker];
      }
    });
  }
}
